import { useInfiniteQuery } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { UrlHelper } from '@/utils/UrlHelper';
import { GetOrderAuthorsLookupList } from '@/features/orders/types/orders-dto';
import { useOrdersFilter } from '@/features/orders/hooks/useOrdersFilter';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {
    search?: string;
} & ApiHookProps;

export const useOrderAuthorsLookupApi = ({ search }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useOrdersFilter();
    const { take, skip } = filter;

    const {
        data: orderAuthorsLookupList, 
        isLoading: isLoadingOrderAuthorsLookupList, 
        isError: isErrorOrderAuthorsLookupList,
        fetchNextPage: fetchOrderAuthorsLookupListNextPage,
        hasNextPage: hasOrderAuthorsLookupListNextPage,
        isFetchingNextPage: isFetchingOrderAuthorsLookupListNextPage,
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
            ApiConfig.queries.features.orders.queryKeys.orderAuthorsLookup, 
            take, skip, search
        ],
        queryFn: async ({pageParam}): Promise<GetOrderAuthorsLookupList> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.orders.endpoints.orderAuthorsLookup, {    
                take, skip: pageParam, searchText: search?.trim()
            });
            const response = await axiosInstance.get<GetOrderAuthorsLookupList>(url);
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: skip ?? 0,
    });

    const fetchedOrderAuthorsLookupList = orderAuthorsLookupList?.pages.flatMap((page) => page.items ?? []);

    return {
        orderAuthorsLookupList: fetchedOrderAuthorsLookupList, 
        isLoadingOrderAuthorsLookupList, 
        isErrorOrderAuthorsLookupList,
        fetchOrderAuthorsLookupListNextPage,
        hasOrderAuthorsLookupListNextPage,
        isFetchingOrderAuthorsLookupListNextPage,
    }
};
